// Frontend wrapper for the EdgeLex AI-first runtime.
// Uses the shared `apiClient` (axios with Keycloak Bearer-token interceptor)
// so requests carry auth automatically. The shared client's baseURL is
// `/api/v1`, so this module calls relative paths under `/lex-runtime/*`.
// The backend bridge (Phase 2 minimum) at `/api/v1/lex-runtime/*` forwards to
// the Python runtime.

import { AxiosError } from 'axios';

import apiClient, { getAuthToken } from '@/api/axios';
import type {
  ChatResumeRequest,
  ChatRuntimeRequest,
  ChatRuntimeResponse,
  LexModelPolicy,
  RuntimeStreamEvent,
} from '../types/runtime';

const LEX_RUNTIME_REQUEST_TIMEOUT_MS = 120_000;

export class LexRuntimeError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'LexRuntimeError';
    this.status = status;
    this.detail = detail;
  }
}

async function _post(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<ChatRuntimeResponse> {
  try {
    const response = await apiClient.post<ChatRuntimeResponse>(path, body, {
      signal,
      timeout: LEX_RUNTIME_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  } catch (err) {
    const axiosErr = err as AxiosError;
    if (axiosErr.response) {
      const data = axiosErr.response.data;
      const detail =
        typeof data === 'object' && data !== null && 'detail' in data
          ? String((data as { detail: unknown }).detail)
          : typeof data === 'string'
            ? data
            : JSON.stringify(data ?? {});
      throw new LexRuntimeError(
        `Lex runtime returned ${axiosErr.response.status}`,
        axiosErr.response.status,
        detail,
      );
    }
    throw new LexRuntimeError(
      axiosErr.message || 'unknown error',
      undefined,
      axiosErr.code,
    );
  }
}

export async function postChat(
  request: ChatRuntimeRequest,
  signal?: AbortSignal,
): Promise<ChatRuntimeResponse> {
  return _post('/lex-runtime/chat', request, signal);
}

function responseFromCompletedEvent(event: RuntimeStreamEvent): ChatRuntimeResponse {
  const payload = event.payload;
  return {
    answer: payload.answer as ChatRuntimeResponse['answer'],
    provenance: (payload.provenance ?? {}) as ChatRuntimeResponse['provenance'],
    audit_events: (payload.audit_events ?? []) as ChatRuntimeResponse['audit_events'],
    intent: (payload.intent ?? {}) as ChatRuntimeResponse['intent'],
    resolved_scope: (payload.resolved_scope ?? {}) as ChatRuntimeResponse['resolved_scope'],
    routing_decision: (payload.routing_decision ?? {}) as ChatRuntimeResponse['routing_decision'],
    focus_matter_id: (payload.focus_matter_id ?? null) as string | null,
    pending_clarification: (payload.pending_clarification ?? null) as ChatRuntimeResponse['pending_clarification'],
    pending_approval: (payload.pending_approval ?? null) as ChatRuntimeResponse['pending_approval'],
    pending_interaction: (payload.pending_interaction ?? null) as ChatRuntimeResponse['pending_interaction'],
    applied_action: (payload.applied_action ?? null) as ChatRuntimeResponse['applied_action'],
    turn_type: (payload.turn_type ?? null) as string | null,
  };
}

function parseSseBlock(block: string): RuntimeStreamEvent | null {
  const dataLines = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart());
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join('\n')) as RuntimeStreamEvent;
  } catch {
    return null;
  }
}

export async function postChatStream(
  request: ChatRuntimeRequest,
  onEvent: (event: RuntimeStreamEvent) => void,
  signal?: AbortSignal,
): Promise<ChatRuntimeResponse> {
  const token = getAuthToken();
  const response = await fetch('/api/v1/lex-runtime/chat/stream', {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => '');
    throw new LexRuntimeError(
      `Lex runtime stream returned ${response.status}`,
      response.status,
      detail,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalTurn: ChatRuntimeResponse | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\n\n/);
    buffer = blocks.pop() ?? '';
    for (const block of blocks) {
      const event = parseSseBlock(block);
      if (!event) continue;
      onEvent(event);
      if (event.event_type === 'answer.completed') {
        finalTurn = responseFromCompletedEvent(event);
      }
    }
  }

  const trailing = parseSseBlock(buffer);
  if (trailing) {
    onEvent(trailing);
    if (trailing.event_type === 'answer.completed') {
      finalTurn = responseFromCompletedEvent(trailing);
    }
  }

  if (!finalTurn) {
    throw new LexRuntimeError('Lex runtime stream ended without answer.completed');
  }
  return finalTurn;
}

export async function postChatResume(
  request: ChatResumeRequest,
  signal?: AbortSignal,
): Promise<ChatRuntimeResponse> {
  return _post('/lex-runtime/chat/resume', request, signal);
}

export async function getMyModelPolicy(signal?: AbortSignal): Promise<LexModelPolicy> {
  try {
    const response = await apiClient.get<LexModelPolicy>('/governance/my-policy', { signal });
    return response.data;
  } catch (err) {
    const axiosErr = err as AxiosError;
    throw new LexRuntimeError(
      axiosErr.message || 'Failed to load model policy',
      axiosErr.response?.status,
    );
  }
}
